import { useState, useRef } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export function VoiceRecorder({ onTranscript, language = "en", testid = "voice-recorder" }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          if (language) fd.append("language", language);
          const { data } = await api.post("/ai/transcribe", fd);
          onTranscript?.(data.text || "");
          toast.success("Transcribed", { description: data.text?.slice(0, 80) });
        } catch (err) {
          toast.error("Couldn't transcribe", { description: formatApiError(err) });
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone unavailable", { description: e.message });
    }
  }

  function stop() {
    mediaRef.current?.stop();
    setRecording(false);
  }

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={transcribing}
      data-testid={testid}
      className={`rounded-full grid place-items-center transition-all ${
        recording
          ? "w-20 h-20 bg-[#C05640] text-white mic-pulse"
          : "w-16 h-16 bg-[#F4F1EA] text-[#C05640] hover:bg-[#E8D8D3]"
      }`}
      aria-label={recording ? "Stop recording" : "Start voice recording"}
    >
      {transcribing ? <Loader2 className="animate-spin" size={24} /> :
        recording ? <Square size={26} fill="currentColor" /> : <Mic size={28} />}
    </button>
  );
}
